/** @jsxImportSource @emotion/react */
import React from "react";
import { useTheme, useThemedCss } from "src/hooks/styles";
import { IconLogo, Logo } from "src/components/ui/logo";
import Navbar from "react-bootstrap/esm/Navbar";
import Text from "src/components/ui/text";
import { useTranslation } from "react-i18next";
import MenuItem from "src/components/ui/menu/MenuItem";
import Icon from "src/components/ui/icons";
import { useAuthStore } from "src/store/account";
import styles, { IStyles } from "src/components/ui/menu/SideMenu.styles";
import { useMenuStore } from "src/store/menu";
import { useBreakpointIsLowerOrEqual } from "src/hooks/responsive";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useLogout } from "src/hooks/account";
import MenuItemDropdown from "src/components/ui/menu/MenuItemDropdown";
import MenuTitle from "src/components/ui/menu/MenuTitle";
import MenuSeparator from "src/components/ui/menu/MenuSeparator";
import { useProfileHasPermission } from "src/hooks/profile";
import { MembershipPermission } from "src/enums/permission";
import { useTutorialStore } from "src/store/tutorial";
import { useStatus } from "src/provider/StatusProvider";
import {
  Home,
  Search,
  TrendingUp,
  Users,
  BookOpen,
  Shield,
  UserCheck,
  CreditCard,
  PlayCircle,
  HelpCircle,
  Settings,
  Heart,
  ShieldAlert,
  ChartLine,
  Mail,
  MapPin,
  Building2,
} from "lucide-react";
import { cn } from "src/lib/utils";
import { ProfileMenuVariation } from "src/components/ui/menu";
import TrialCountdown from "src/components/trial-countdown";

type SideMenuProps = React.HTMLProps<HTMLDivElement> & {
  style?: React.CSSProperties;
  className?: string;
  mobileMenuContent?: React.ReactNode;
};

const SideMenu: React.FC<SideMenuProps> = ({ style, mobileMenuContent, ...props }) => {
  const { t } = useTranslation();
  const isMobile = useBreakpointIsLowerOrEqual("sm");
  const {
    header: { height: headerHeight },
    menu: { sideWidth, sideWidthOpen },
  } = useTheme();
  const location = useLocation();

  const { isAuthenticated, isSuperuser } = useAuthStore();

  const hasAgentAccess = useProfileHasPermission(MembershipPermission.ACCESS_AGENT_DASHBOARD);

  const { isOpen, toggle: toggleMenu, setIsOpen } = useMenuStore();

  const showMarketResearchV2 = useProfileHasPermission(MembershipPermission.ACCESS_PROPERTY_ARBITRAGE_VERIFIED_LIST);

  const { viewsets } = useStatus();

  const themedCss = useThemedCss<IStyles>(styles);

  const { setReadyComponents } = useTutorialStore();

  React.useEffect(() => {
    setReadyComponents(["#layout-aside"]);
  }, []);

  const containerStyles = React.useMemo<React.CSSProperties>(() => {
    if (isMobile) {
      if (!isOpen) {
        return {
          minWidth: sideWidth,
          maxWidth: sideWidth,
          width: sideWidth,
          minHeight: 0,
          boxShadow: "none",
          height: headerHeight,
        };
      }

      return {
        minWidth: "90vw",
        maxWidth: "90vw",
        width: "90vw",
      };
    }
    if (!isOpen) {
      return {
        minWidth: sideWidth,
        maxWidth: sideWidth,
        width: sideWidth,
      };
    }
    return {
      minWidth: sideWidthOpen,
      maxWidth: sideWidthOpen,
      width: sideWidthOpen,
    };
  }, [isOpen, isMobile]);

  const toggleStyles = React.useMemo<React.CSSProperties>(() => {
    if (!isOpen) {
      return {
        transform: "translate(-50%, -50%) rotate(180deg)",
      };
    }
    return {};
  }, [isOpen]);

  const navbarStyles = React.useMemo<React.CSSProperties>(() => {
    if (isMobile && !isOpen) {
      return {
        display: "flex",
        justifyContent: "center",
        borderRight: "1px solid #E7E7E7",
      };
    }
    if (isOpen) {
      return {
        paddingLeft: 40,
      };
    }
    return {
      display: "flex",
      justifyContent: "center",
    };
  }, [isOpen, isMobile]);

  const handleLogout = useLogout();

  React.useEffect(() => {
    if (isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleLogoClick: React.MouseEventHandler = React.useCallback(
    (event) => {
      if (isMobile && !isOpen) {
        setIsOpen(true);
        event.preventDefault();
      }
    },
    [isMobile, isOpen],
  );

  if (viewsets === "new") {
    return (
      <>
        <aside id="layout-aside" css={themedCss.container} style={{ ...style, ...containerStyles }} {...props}>
          <Navbar css={themedCss.navbar} style={navbarStyles}>
            <Text.Link to="/" onClick={handleLogoClick}>
              {isOpen ? <Logo /> : <IconLogo />}
            </Text.Link>
            <button type="button" css={themedCss.toggle} onClick={toggleMenu} style={toggleStyles}>
              <span css={themedCss.toggleIcon}>
                <Icon name="arrow-left" size={10} variant="inherit" />
              </span>
            </button>
          </Navbar>
          {(!isMobile || (isMobile && isOpen)) && (
            <div css={themedCss.menuContainer} className={cn("px-2", isOpen && "px-6")}>
              {isMobile && mobileMenuContent && mobileMenuContent}
              <div css={themedCss.menu} className={cn("flex flex-col items-center justify-start", isOpen && "items-start justify-start")}>
                {/* Main Navigation */}
                <TrialCountdown className="mb-2 w-full" minimize={!isOpen && !isMobile} />
                <MenuTitle title="Arbitrage Hub" isOpen={isOpen} />
                <MenuItem
                  to="/arbitrage-properties/"
                  title={t("components.side_menu.dashboard_title", "Properties")}
                  icon={<Home size={15} className="text-foreground/80" />}
                  id="side-menu-home-link"
                  isOpen={isOpen}
                  className="mb-0"
                />
                <MenuItem
                  to="/apartments/"
                  title={t("components.side_menu.dashboard_title", "Apartments")}
                  icon={<Building2 size={15} className="text-foreground/80" />}
                  id="side-menu-home-link"
                  isOpen={isOpen}
                  className="mb-0"
                />
                <MenuItem
                  to="/co-host-properties/"
                  title={t("components.side_menu.dashboard_title", "Co-host")}
                  icon={<Users size={15} className="text-foreground/80" />}
                  id="side-menu-home-link"
                  isOpen={isOpen}
                  className="mb-0"
                />
                <MenuItem
                  to="/maria/"
                  title={t("components.side_menu.dashboard_title", "Lease Portal")}
                  icon={<Search size={15} className="text-foreground/80" />}
                  id="side-menu-lease-portal-link"
                  isOpen={isOpen}
                  className="mb-0"
                  authNeeded
                />
                <MenuItem
                  to="/deals/"
                  title={t("components.side_menu.your_deals_title", "Your deals")}
                  icon={<Heart size={15} className="text-foreground/80" />}
                  id="side-menu-deals-link"
                  isOpen={isOpen}
                  className="mb-0"
                />

                <MenuSeparator isOpen={isOpen} />

                {/* Research & Analysis */}
                <MenuTitle title="Research Hub" isOpen={isOpen} />
                <MenuItem
                  to="/str-database/"
                  title={t("components.side_menu.ordinances_title", "See Ordinances")}
                  icon={<ShieldAlert size={15} className="text-foreground/80" />}
                  id="side-menu-str-database-link"
                  isOpen={isOpen}
                  className="mb-0"
                  authNeeded
                />
                <MenuItem
                  to="/local-pulse/"
                  title={t("components.side_menu.ordinances_title", "Analyze Nearby")}
                  icon={<MapPin size={15} className="text-foreground/80" />}
                  id="side-menu-local-pulse-link"
                  isOpen={isOpen}
                  className="mb-0"
                />
                <MenuItem
                  to="/arbitrage-calculator/"
                  title={t("components.side_menu.analyze_property_title", "Analyze Property")}
                  icon={<ChartLine size={15} className="text-foreground/80" />}
                  id="side-menu-analyze-property-link"
                  isOpen={isOpen}
                  className="mb-0"
                  free
                />
                <MenuItem
                  to="/market-research/"
                  title={t("components.side_menu.find_properties_title", "Analyze Market")}
                  icon={<TrendingUp size={15} className="text-foreground/80" />}
                  isOpen={isOpen}
                  id="side-menu-analyze-market-link"
                  className="mb-0"
                />

                <MenuSeparator isOpen={isOpen} />

                {/* Services & Learning */}
                <MenuTitle title="Services & Learning Hub" isOpen={isOpen} />
                <MenuItem
                  to="/vendors/"
                  title={t("components.side_menu.vendors_title", "Find Vendor")}
                  icon={<Users size={15} className="text-foreground/80" />}
                  isOpen={isOpen}
                  className="mb-0"
                  id="side-menu-find-vendor-link"
                  free
                />
                <MenuItem
                  to="/learning-hub/"
                  title={t("components.side_menu.learning_hub_title", "Courses")}
                  icon={<BookOpen size={15} className="text-foreground/80" />}
                  id="side-menu-learning-hub-link"
                  isOpen={isOpen}
                  className="mb-0"
                />
                <MenuItem
                  id="side-menu-message-link"
                  to="/message/"
                  title={t("components.side_menu.message_title", "Message")}
                  icon={<Mail size={15} className="text-foreground/80" />}
                  isOpen={isOpen}
                  className="mb-0"
                  authNeeded
                />

                <MenuSeparator isOpen={isOpen} />
                <MenuTitle title="Account & Support" isOpen={isOpen} />
                {isAuthenticated && isSuperuser && (
                  <MenuItem
                    to="/admin/"
                    title={t("components.side_menu.admin_panel_title", "Admin Panel")}
                    icon={<Shield size={16} className="text-foreground/80" />}
                    isOpen={isOpen}
                    className="mb-0"
                  />
                )}
                {isAuthenticated && hasAgentAccess && (
                  <MenuItem
                    to="/agent/"
                    title={t("components.side_menu.agent_panel_title", "Agent Panel")}
                    icon={<UserCheck size={16} className="text-foreground/80" />}
                    isOpen={isOpen}
                    className="mb-0"
                  />
                )}
                <MenuItem
                  to="/membership/select/"
                  title={t("components.side_menu.membership_title", "Subscription")}
                  icon={<CreditCard size={15} className="text-foreground/80" />}
                  isOpen={isOpen}
                  className="mb-0"
                  authNeeded
                />
                <MenuItem
                  to="/getting-started/"
                  title={t("components.side_menu.getting_started_title", "Getting Started")}
                  icon={<PlayCircle size={15} className="text-foreground/80" />}
                  id="side-menu-getting-started-link"
                  isOpen={isOpen}
                  className="mb-0"
                  authNeeded
                />
                <MenuItem
                  to="/help/"
                  title={t("components.side_menu.help_title", "Help")}
                  icon={<HelpCircle size={15} className="text-foreground/80" />}
                  isOpen={isOpen}
                  className="mb-0"
                  authNeeded
                />
                <MenuItem
                  to="/settings/profile/"
                  title={t("components.side_menu.profile_title", "Settings")}
                  icon={<Settings size={15} className="text-foreground/80" />}
                  isOpen={isOpen}
                  className="mb-0"
                  authNeeded
                />
              </div>
              {isAuthenticated && (
                <div css={themedCss.footerMenu}>
                  <ProfileMenuVariation />
                </div>
              )}
            </div>
          )}
        </aside>

        {isMobile && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18 }}
            css={themedCss.overlay}
            onClick={() => setIsOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <aside id="layout-aside" css={themedCss.container} style={{ ...style, ...containerStyles }} {...props}>
        <Navbar css={themedCss.navbar} style={navbarStyles}>
          <Text.Link to="/" onClick={handleLogoClick}>
            {isOpen ? <Logo /> : <IconLogo />}
          </Text.Link>
          <button type="button" css={themedCss.toggle} onClick={toggleMenu} style={toggleStyles}>
            <span css={themedCss.toggleIcon}>
              <Icon name="arrow-left" size={10} variant="inherit" />
            </span>
          </button>
        </Navbar>
        {(!isMobile || (isMobile && isOpen)) && (
          <div css={themedCss.menuContainer}>
            {isMobile && mobileMenuContent && mobileMenuContent}
            <div css={themedCss.menu}>
              {/* Main Navigation */}
              <MenuTitle title="Main" isOpen={isOpen} />
              <MenuItem
                to="/"
                title={t("components.side_menu.dashboard_title", "Dashboard")}
                iconName="home-up"
                id="side-menu-dashboard-link"
                isOpen={isOpen}
                className="mb-0"
              />

              <MenuSeparator isOpen={isOpen} />

              {/* Property Management */}
              <MenuTitle title="Property Management" isOpen={isOpen} />
              <MenuItemDropdown
                title={t("components.side_menu.rental_hub_title", "Rental hub")}
                iconName="building"
                isOpen={isOpen}
                id="side-menu-rental-hub-dropdown"
              >
                <MenuItem
                  to="/arbitrage-properties/"
                  title={t("components.side_menu.arbitrage_properties_title", "properties")}
                  iconName="property-search"
                  isOpen={isOpen}
                />
                <MenuItem
                  to="/deals/"
                  title={t("components.side_menu.your_deals_title", "Your deals")}
                  iconName="note"
                  isOpen={isOpen}
                  id="side-menu-deals-link"
                />
              </MenuItemDropdown>

              <MenuSeparator isOpen={isOpen} />

              {/* Research & Analysis */}
              <MenuTitle title="Research & Analysis" isOpen={isOpen} />
              <MenuItemDropdown
                title={t("components.side_menu.research_hub_title", "Research hub")}
                iconName="search"
                id="side-menu-research-hub-dropdown"
                isOpen={isOpen}
              >
                <MenuItem
                  to="/local-pulse/"
                  title={t("components.side_menu.local_pulse_title", "Local pulse")}
                  iconName="shining"
                  id="side-menu-local-pulse-link"
                  isOpen={isOpen}
                />
                <MenuItem
                  to="/str-database/"
                  title={t("components.side_menu.ordinances_title", "Ordinances")}
                  iconName="documents"
                  id="side-menu-ordinances-link"
                  isOpen={isOpen}
                />
                <MenuItem
                  to="/properties/"
                  title={t("components.side_menu.find_properties_title", "Market research")}
                  iconName="pie-chart"
                  isOpen={isOpen}
                  id="rental-markets-menu-item"
                />
                {showMarketResearchV2 && (
                  <MenuItem
                    to="/market-research/"
                    title={t("components.side_menu.find_properties_title", "Market research V2")}
                    iconName="pie-chart"
                    isOpen={isOpen}
                    id="rental-markets-menu-item"
                  />
                )}
                <MenuItem
                  to="/arbitrage-calculator/"
                  title={t("components.side_menu.analyze_property_title", "Analyze property")}
                  iconName="chart"
                  id="side-menu-analyze-property-link"
                  isOpen={isOpen}
                />
              </MenuItemDropdown>

              <MenuSeparator isOpen={isOpen} />

              {/* Services & Partners */}
              <MenuTitle title="Services & Partners" isOpen={isOpen} />
              <MenuItemDropdown
                title={t("components.side_menu.service_providers_title", "Service")}
                iconName="time-oclock"
                id="side-menu-providers-dropdown"
                isOpen={isOpen}
              >
                <MenuItem to="/vendors/" title={t("components.side_menu.vendors_title", "Management")} iconName="home-up" isOpen={isOpen} />
                <MenuItem
                  to="/partners/"
                  title={t("components.side_menu.partners_title", "Agents & Coaches")}
                  iconName="paper-clip"
                  isOpen={isOpen}
                />
                <MenuItem
                  to="/my-people/"
                  title={t("components.side_menu.my_people_title", "My people")}
                  iconName="heart"
                  isOpen={isOpen}
                />
              </MenuItemDropdown>
              <MenuItemDropdown title={t("components.side_menu.business_title", "Business")} iconName="business" isOpen={isOpen}>
                <MenuItem
                  to="/business/portfolio/"
                  title={t("components.side_menu.business_profile_title", "Portfolio")}
                  iconName="business"
                  isOpen={isOpen}
                  menuLevel={2}
                />
                <MenuItem
                  to="/business/settings/"
                  title={t("components.side_menu.business_settings_title", "Settings")}
                  iconName="gear"
                  isOpen={isOpen}
                  menuLevel={2}
                />
              </MenuItemDropdown>

              <MenuSeparator isOpen={isOpen} />

              {/* Communication & Learning */}
              <MenuTitle title="Communication & Learning" isOpen={isOpen} />
              <MenuItem
                to="/message/"
                title={t("components.side_menu.message_title", "Message")}
                iconName="message-square"
                id="side-menu-message-link"
                isOpen={isOpen}
                className="mb-0"
              />
              <MenuItem
                to="/learning-hub/"
                title={t("components.side_menu.learning_hub_title", "Learning hub")}
                iconName="pie-chart"
                id="side-menu-learning-hub-link"
                isOpen={isOpen}
                className="mb-0"
              />
              <MenuItemDropdown title={t("components.side_menu.account_title", "Settings")} iconName="gear" isOpen={isOpen}>
                <MenuItem
                  to="/settings/profile/"
                  title={t("components.side_menu.profile_title", "Profile")}
                  iconName="profile"
                  isOpen={isOpen}
                  menuLevel={2}
                />
                <MenuItem
                  to="/membership/select/"
                  title={t("components.side_menu.membership_title", "Subscription")}
                  iconName="documents"
                  isOpen={isOpen}
                  menuLevel={2}
                />
              </MenuItemDropdown>

              {/* Admin & Agent Access */}
              {(isAuthenticated && isSuperuser) || (isAuthenticated && hasAgentAccess) ? (
                <>
                  <MenuSeparator isOpen={isOpen} />
                  <MenuTitle title="Administration" isOpen={isOpen} />
                  {isAuthenticated && isSuperuser && (
                    <MenuItem
                      to="/admin/"
                      title={t("components.side_menu.admin_panel_title", "Admin panel")}
                      iconName="lock"
                      isOpen={isOpen}
                      className="mb-0"
                    />
                  )}
                  {isAuthenticated && hasAgentAccess && (
                    <MenuItem
                      to="/agent/"
                      title={t("components.side_menu.agent_panel_title", "Agent panel")}
                      iconName="lock"
                      isOpen={isOpen}
                      className="mb-0"
                    />
                  )}
                </>
              ) : null}
            </div>
            {isAuthenticated && (
              <div css={themedCss.footerMenu}>
                <MenuSeparator isOpen={isOpen} />
                <MenuTitle title="Account & Support" isOpen={isOpen} />
                <MenuItem to="/help/" title={t("components.side_menu.help_title", "Help")} iconName="help" isOpen={isOpen} />
                <MenuItem
                  onClick={handleLogout}
                  title={t("components.side_menu.log_out_title", "Log out")}
                  iconName="exit"
                  isOpen={isOpen}
                  className="mb-0"
                />
              </div>
            )}
          </div>
        )}
      </aside>

      {isMobile && isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          exit={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.18 }}
          css={themedCss.overlay}
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default SideMenu;
